import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UploadCloud, FileLock2, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAdicionarCertificado } from '@/services/Configuracoes';

const ACCEPTED_EXTENSIONS = ['.pfx', '.p12'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdicionarCertificadoDialog({ open, onOpenChange }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const { mutate, isPending, isError, data, reset } = useAdicionarCertificado();

  const succeeded = data?.status.resultado === true;
  const failed = isError || data?.status.resultado === false;

  function resetState() {
    setFile(null);
    setSenha('');
    setDragOver(false);
    reset();
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  }

  function validateAndSet(candidate: File) {
    const ext = candidate.name.slice(candidate.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) return;
    setFile(candidate);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) validateAndSet(dropped);
  }

  function handleSubmit() {
    if (!file || !senha) return;
    mutate({ file, senha });
  }

  const serverMessage = data?.status.mensagem;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Certificado</DialogTitle>
        </DialogHeader>

        {succeeded ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <p className="font-semibold text-foreground">Certificado adicionado com sucesso!</p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div
              role="button"
              tabIndex={0}
              aria-label="Área de upload — clique ou arraste um arquivo"
              className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors cursor-pointer',
                dragOver
                  ? 'border-accent bg-accent/10'
                  : 'border-muted-foreground/25 hover:border-accent hover:bg-accent/5'
              )}
              onClick={() => document.getElementById('certificado-file-input')?.click()}
              onKeyDown={e =>
                e.key === 'Enter' && document.getElementById('certificado-file-input')?.click()
              }
              onDragOver={e => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <UploadCloud className="h-10 w-10 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">
                  Clique para selecionar ou arraste aqui
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Somente arquivos <span className="font-semibold">.pfx</span> ou{' '}
                  <span className="font-semibold">.p12</span>
                </p>
              </div>
            </div>

            <input
              id="certificado-file-input"
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              className="hidden"
              onChange={e => {
                const selected = e.target.files?.[0];
                if (selected) validateAndSet(selected);
                e.target.value = '';
              }}
            />

            {file && (
              <div className="flex items-center gap-3 rounded-md border bg-muted/40 px-3 py-2">
                <FileLock2 className="h-5 w-5 shrink-0 text-accent" />
                <span className="flex-1 truncate text-sm text-foreground">{file.name}</span>
                <button
                  type="button"
                  aria-label="Remover arquivo"
                  onClick={() => setFile(null)}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="certificado-senha">Senha do certificado</Label>
              <Input
                id="certificado-senha"
                type="password"
                value={senha}
                onChange={e => setSenha(e.target.value)}
                disabled={isPending}
                autoComplete="off"
              />
            </div>

            {isPending && (
              <p className="text-xs text-muted-foreground text-center">Enviando certificado...</p>
            )}

            {failed && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{serverMessage ?? 'Não foi possível adicionar o certificado.'}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {succeeded ? (
            <Button onClick={() => handleOpenChange(false)}>Fechar</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={!file || !senha || isPending}>
                {isPending ? 'Enviando...' : 'Enviar'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
